import React, { useState, useEffect, useRef } from 'react';
import './BottomNav.css';

import nav_icon_order from '../../../../img/nav-icon-order.svg';
import nav_icon_children from '../../../../img/nav-icon-children.svg';
import nav_link_profile from '../../../../img/nav-link-profile.svg';
import nav_link_massege from '../../../../img/nav-link-massege.svg';

const NAV_ITEMS = [
  {
    id: 'applications',
    label: 'Заявки',
    icon: nav_icon_order,
    hasSheet: true,
  },
  {
    id: 'children',
    label: 'Діти',
    icon: nav_icon_children,
  },
  {
    id: 'messages',
    label: 'Листи',
    icon: nav_link_massege,
    badgeKey: 'messages',
  },
  {
    id: 'profile',
    label: 'Профіль',
    icon: nav_link_profile,
  },
  {
    id: 'more',
    label: 'Ще',
    icon: null, // dots icon via CSS
  },
];

const SHEET_ITEMS = [
  { id: 'applications_act',     label: 'Активні заявки',    icon: '✓' },
  { id: 'applications_closed',  label: 'Завершені заявки',  icon: '○' },
  { id: 'applications_removed', label: 'Видалені заявки',   icon: '✕' },
];

const MORE_ITEMS = [
  { id: 'faq',         label: 'Питання та відповіді', iconClass: 'bn-more-icon-faq' },
  { id: 'appointment', label: 'Запис на прийом',      iconClass: 'bn-more-icon-calendar' },
  { id: 'knowledgebase', label: 'База знань',         iconClass: 'bn-more-icon-book', external: true },
];

const APPLICATION_IDS = new Set([
  'applications', 'applications_act', 'applications_closed', 'applications_removed'
]);

const BottomNav = ({ activeItem, onMenuItemClick, onNewApplication, messagesInfo }) => {
  const [sheetOpen, setSheetOpen]   = useState(false);
  const [moreOpen,  setMoreOpen]    = useState(false);
  const sheetRef = useRef(null);
  const moreRef  = useRef(null);

  // Close panels on outside touch
  useEffect(() => {
    const handler = (e) => {
      if (sheetOpen && sheetRef.current && !sheetRef.current.contains(e.target)) {
        setSheetOpen(false);
      }
      if (moreOpen && moreRef.current && !moreRef.current.contains(e.target)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener('touchstart', handler);
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('touchstart', handler);
      document.removeEventListener('mousedown', handler);
    };
  }, [sheetOpen, moreOpen]);

  // Close sheet/more when active item changes
  useEffect(() => {
    setSheetOpen(false);
    setMoreOpen(false);
  }, [activeItem]);

  const getNavActive = (itemId) => {
    if (itemId === 'applications') return APPLICATION_IDS.has(activeItem);
    if (itemId === 'more') return MORE_ITEMS.some(m => m.id === activeItem);
    return activeItem === itemId;
  };

  const handleNavClick = (item) => {
    if (item.id === 'more') {
      setSheetOpen(false);
      setMoreOpen(prev => !prev);
      return;
    }

    if (item.hasSheet) {
      setMoreOpen(false);
      // If already on applications — toggle sheet to pick sub-item
      if (APPLICATION_IDS.has(activeItem)) {
        setSheetOpen(prev => !prev);
      } else {
        onMenuItemClick('applications_act');
        setSheetOpen(true);
      }
      return;
    }

    setSheetOpen(false);
    setMoreOpen(false);
    onMenuItemClick(item.id);
  };

  const handleSheetItem = (id) => {
    setSheetOpen(false);
    onMenuItemClick(id);
  };

  const handleMoreItem = (item) => {
    setMoreOpen(false);
    if (item.external) return; // knowledge base — href handled by link
    onMenuItemClick(item.id);
  };

  const msgBadge = messagesInfo?.all > 0 ? messagesInfo.all : null;

  return (
    <>
      {/* ── Overlays ── */}
      {(sheetOpen || moreOpen) && (
        <div
          className="bn-overlay"
          onClick={() => { setSheetOpen(false); setMoreOpen(false); }}
        />
      )}

      {/* ── Applications bottom sheet ── */}
      <div className={`bn-sheet ${sheetOpen ? 'bn-sheet--open' : ''}`} ref={sheetRef} role="dialog" aria-label="Підрозділи заявок">
        <div className="bn-sheet__handle" />
        <p className="bn-sheet__title">Заявки на вступ</p>

        <button className="bn-sheet__new" onClick={() => { setSheetOpen(false); onNewApplication(); }}>
          <span className="bn-sheet__new-plus">+</span> Нова заявка
        </button>

        <div className="bn-sheet__items">
          {SHEET_ITEMS.map(item => (
            <button
              key={item.id}
              className={`bn-sheet__item ${activeItem === item.id || (item.id === 'applications_act' && activeItem === 'applications') ? 'bn-sheet__item--active' : ''}`}
              onClick={() => handleSheetItem(item.id)}
            >
              <span className="bn-sheet__item-icon">{item.icon}</span>
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── "More" bottom sheet ── */}
      <div className={`bn-sheet ${moreOpen ? 'bn-sheet--open' : ''}`} ref={moreRef} role="dialog" aria-label="Додаткове меню">
        <div className="bn-sheet__handle" />
        <div className="bn-sheet__items">
          {MORE_ITEMS.map(item =>
            item.external ? (
              <a key={item.id} className="bn-sheet__item bn-sheet__item--link" href="#" onClick={() => setMoreOpen(false)}>
                <span className={`bn-sheet__item-dot ${item.iconClass}`} />
                {item.label}
                <span className="bn-sheet__item-ext">↗</span>
              </a>
            ) : (
              <button
                key={item.id}
                className={`bn-sheet__item ${activeItem === item.id ? 'bn-sheet__item--active' : ''}`}
                onClick={() => handleMoreItem(item)}
              >
                <span className={`bn-sheet__item-dot ${item.iconClass}`} />
                {item.label}
              </button>
            )
          )}
        </div>
      </div>

      {/* ── Bottom navigation bar ── */}
      <nav className="bottom-nav" aria-label="Мобільна навігація">
        {NAV_ITEMS.map(item => {
          const isActive = getNavActive(item.id);
          return (
            <button
              key={item.id}
              className={`bottom-nav__item ${isActive ? 'bottom-nav__item--active' : ''}`}
              onClick={() => handleNavClick(item)}
              aria-label={item.label}
              aria-current={isActive ? 'page' : undefined}
            >
              {item.icon ? (
                <img
                  src={item.icon}
                  alt=""
                  className="bottom-nav__icon"
                  style={isActive ? { filter: 'invert(27%) sepia(99%) saturate(2000%) hue-rotate(210deg) brightness(100%)' } : {}}
                />
              ) : (
                <span className="bottom-nav__dots" aria-hidden="true">•••</span>
              )}

              {item.id === 'messages' && msgBadge && (
                <span className="bottom-nav__badge" aria-label={`${msgBadge} повідомлень`}>
                  {msgBadge > 99 ? '99+' : msgBadge}
                </span>
              )}

              <span className="bottom-nav__label">{item.label}</span>
            </button>
          );
        })}
      </nav>
    </>
  );
};

export default BottomNav;